import { useEffect, useState } from 'react'
import { Button, Input, Select } from '../ui'
import { originValues, originSummary, ORIGIN_KINDS } from '../../lib/bottleOrigin'
import SourceNamePicker from './SourceNamePicker'
import {
  BOTTLE_FORMATS,
  FORMAT_DEFAULT_SIZE,
  FORMAT_SIZES,
  MAX_ON_HAND,
  bottleFormatSummary,
  clampOnHand,
  cleanSizeInput,
  formatMl,
  isCountedFormat,
  parseBottleSize,
  suggestedFormat,
  totalMl,
} from '../../lib/bottleFormat'

// Bought from a store or website: pick it from the shared list (FOS-SOURCE-PICKER-KEYBOARD-001).
// Gifts, swaps and the rest keep a typed name, since those are people, not stores.
const STORE_KINDS = new Set(['retailer', 'online'])
const nameLabel = kind => kind === 'gift' ? 'Who gave it?' : "Store or person's name"

// Only the after-add question guesses (a bottle of 3 ml or less is almost always a sample);
// an existing bottle shows exactly what was saved.
function formatValues(initial = {}, suggest = false) {
  const format = suggest ? suggestedFormat(initial) : (initial.bottle_format || null)
  const size = initial.size_ml != null ? Number(initial.size_ml) : (isCountedFormat(format) ? FORMAT_DEFAULT_SIZE[format] : null)
  return { bottle_format: format, size_ml: size == null ? '' : String(size), on_hand: clampOnHand(initial.on_hand ?? 1), bottle_id: initial.bottle_id || null }
}

function summaryOf(values) {
  return [bottleFormatSummary(values), originSummary(values)].filter(Boolean).join(' · ')
}

// What kind of bottle (FOS-SAMPLE-TRAVEL-001): Full bottle / Travel / Sample, then for travel
// and samples the size (tap a chip, or type one) and how many.
function BottleFormatFields({ values, update }) {
  const format = values.bottle_format
  const counted = isCountedFormat(format)
  const chips = counted ? FORMAT_SIZES[format] : []
  const typedIsChip = chips.some(size => String(size) === String(values.size_ml))
  const count = clampOnHand(values.on_hand)
  let total = null
  try { total = counted && count > 1 ? totalMl(parseBottleSize(values.size_ml), count) : null } catch { total = null }
  const choose = next => {
    if (next === format) { update({ bottle_format: null }); return }
    const patch = { bottle_format: next }
    if (isCountedFormat(next) && !FORMAT_SIZES[next].some(size => String(size) === String(values.size_ml))) {
      const current = Number(values.size_ml)
      patch.size_ml = current > 0 && (next === 'sample' ? current <= 5 : current >= 4 && current <= 30) ? values.size_ml : String(FORMAT_DEFAULT_SIZE[next])
    }
    update(patch)
  }
  return <div className="flex flex-col gap-3" data-bottle-format>
    <div>
      <p className="mb-1.5 text-sm text-cream">What kind of bottle?</p>
      <div role="radiogroup" aria-label="What kind of bottle?" className="grid grid-cols-3 gap-1.5">
        {BOTTLE_FORMATS.map(([value, label]) => <button key={value} type="button" role="radio" aria-checked={format === value}
          onClick={() => choose(value)}
          className={`min-h-11 rounded-xl border px-2 text-sm ${format === value ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/70'}`}>
          {label}
        </button>)}
      </div>
    </div>
    {counted && <>
      <div>
        <p className="mb-1.5 text-sm text-cream">Size</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map(size => <button key={size} type="button" aria-pressed={String(values.size_ml) === String(size)}
            onClick={() => update({ size_ml: String(size) })}
            className={`pill min-h-11 border px-3 text-sm ${String(values.size_ml) === String(size) ? 'border-gold bg-gold/10 text-gold' : 'border-white/15 text-cream/60'}`}>
            {formatMl(size)}
          </button>)}
          <label className="flex items-center gap-1 text-sm text-cream/60">
            <input type="text" inputMode="decimal" aria-label="Other size in ml" placeholder="Other"
              value={typedIsChip ? '' : values.size_ml} onChange={event => update({ size_ml: cleanSizeInput(event.target.value) })}
              className="h-11 w-20 rounded-lg border border-white/15 bg-charcoal-700/60 px-2 text-center text-base text-cream outline-none focus:border-gold/40" />
            ml
          </label>
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-sm text-cream">How many {format === 'sample' ? 'samples' : 'travel sprays'}?</p>
        <div className="flex items-center gap-3">
          <button type="button" aria-label="One fewer" disabled={count <= 1} onClick={() => update({ on_hand: count - 1 })}
            className="h-11 w-11 rounded-full border border-white/15 text-lg text-cream disabled:opacity-30">−</button>
          <span aria-live="polite" aria-label={`${count} on hand`} className="min-w-8 text-center text-lg text-cream">{count}</span>
          <button type="button" aria-label="One more" disabled={count >= MAX_ON_HAND} onClick={() => update({ on_hand: count + 1 })}
            className="h-11 w-11 rounded-full border border-white/15 text-lg text-cream disabled:opacity-30">+</button>
          {total != null && <span className="text-sm text-cream/60">{formatMl(total)} total</span>}
        </div>
        {values.on_hand_saved > count && <p className="mt-1 text-xs text-cream/50">Saving marks {values.on_hand_saved - count} as given away.</p>}
      </div>
    </>}
  </div>
}

export default function BottleOrigin({ initial = {}, onSave, onSkip, postAdd = false, offerGiftMemory = false }) {
  const [values, setValues] = useState(() => ({ ...originValues(initial), ...formatValues(initial, postAdd), on_hand_saved: initial.bottle_format ? clampOnHand(initial.on_hand ?? 1) : null }))
  const [editing, setEditing] = useState(postAdd || !summaryOf({ ...originValues(initial), ...(initial.bottle_format ? formatValues(initial) : {}) }))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const update = (key, value) => setValues(current => typeof key === 'object' ? { ...current, ...key } : { ...current, [key]: value })
  const save = async () => {
    if (isCountedFormat(values.bottle_format)) {
      try { if (parseBottleSize(values.size_ml) == null) throw new Error('Pick the size, like 1.5 ml.') }
      catch (sizeError) { setError(sizeError.message); return }
    }
    setSaving(true); setError('')
    try {
      const saved = await onSave(values)
      setValues(current => ({ ...current, ...originValues(saved), bottle_id: saved?.bottle_id ?? current.bottle_id, on_hand_saved: current.bottle_format ? clampOnHand(current.on_hand) : null }))
      setEditing(false)
    }
    catch { setError('Your bottle is saved. These details could not be saved. Try again later.') }
    finally { setSaving(false) }
  }
  const summary = summaryOf(values)
  return <section aria-label="Where did it come from?" className="card p-4 mb-4">
    <div className="flex items-center justify-between gap-2">
      <h2 className="font-semibold text-cream">Where did it come from?</h2>
      {!editing && <button className="min-h-11 px-2 text-gold" onClick={() => setEditing(true)}>Edit</button>}
    </div>
    {!editing ? <p className="text-sm text-cream/60 truncate" title={summary}>{summary || 'No details added'}</p> : <div className="flex flex-col gap-3 mt-2">
      <p className="text-xs text-cream/50">Optional · Only visible to you</p>
      <BottleFormatFields values={values} update={patch => update(patch)} />
      <Select aria-label="Source" label="Source" value={values.acquired_from_kind || ''} onChange={e => { const kind = e.target.value; update(STORE_KINDS.has(kind) === STORE_KINDS.has(values.acquired_from_kind) ? { acquired_from_kind: kind } : { acquired_from_kind: kind, acquired_from_name: '' }) }} options={[{value: '', label: 'Not specified'}, ...ORIGIN_KINDS.map(([value, label]) => ({value, label}))]} />
      {STORE_KINDS.has(values.acquired_from_kind)
        ? <SourceNamePicker key={values.acquired_from_kind} value={values.acquired_from_name || ''} onChange={name => update('acquired_from_name', name)} />
        : <Input aria-label={nameLabel(values.acquired_from_kind)} label={nameLabel(values.acquired_from_kind)} placeholder={values.acquired_from_kind === 'gift' ? 'Their name' : 'Macy’s, Jomashop, or a name'} value={values.acquired_from_name || ''} onChange={e => update('acquired_from_name', e.target.value)} />}
      {offerGiftMemory && values.acquired_from_kind === 'gift' && (values.acquired_from_name || '').trim() && <label className="flex min-h-11 items-center justify-between gap-3 text-sm text-cream" data-gift-memory>
        <span>Also save to your Memory Log<span className="block text-xs text-cream/45">Received from {(values.acquired_from_name || '').trim()} · Only visible to you</span></span>
        <input type="checkbox" className="h-5 w-5 accent-gold" checked={values.remember_gift !== false} onChange={e => update('remember_gift', e.target.checked)} />
      </label>}
      <Input aria-label="Acquired on" label="Acquired on" type="date" value={values.acquired_on || ''} onChange={e => update('acquired_on', e.target.value)} />
      {['is_tester', 'is_blind_buy'].map(key => <label key={key} className="flex min-h-11 items-center justify-between text-sm text-cream">
        {key === 'is_tester' ? 'Tester' : 'Blind buy'}
        <input type="checkbox" role="switch" className="h-5 w-5 accent-gold" checked={values[key] === true} onChange={e => update(key, e.target.checked)} />
      </label>)}
      {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
      <div className="flex gap-3">
        {onSkip ? <Button variant="ghost" onClick={onSkip}>Skip</Button> : <Button variant="ghost" disabled={saving} onClick={() => {setValues({ ...originValues(initial), ...formatValues(initial), on_hand_saved: values.on_hand_saved }); setEditing(false)}}>Cancel</Button>}
        <Button disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save details'}</Button>
      </div>
    </div>}
  </section>
}

export function PrivateBottleOrigin({ id, client, load, save }) {
  const [values, setValues] = useState(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    let active = true
    setValues(null); setError(false)
    load(client, id).then(data => { if (active) setValues(data) }).catch(() => { if (active) setError(true) })
    return () => { active = false }
  }, [id, client, load])
  if (error) return <p className="p-4 text-sm text-cream/50">Origin details are temporarily unavailable.</p>
  if (!values) return null
  return <BottleOrigin key={id} initial={values} onSave={async next => {const saved = await save(client, id, next); setValues(saved); return saved}} />
}
